/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import RetentionWaterfall from "./RetentionWaterfall";

const G = "#0077B5";
const GRN = "#1D9E75";
const CARD = "#FFFFFF";
const B = "#E1E9EE";
const RED = "#D85A30";
const RED2 = "#FEF2F2";

const API_BASE = process.env.REACT_APP_API_URL || "https://go.brandhives.com";

const SectionLabel = ({ children }) => (
  <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, letterSpacing: "0.2em", color: "#999999", textTransform: "uppercase", marginBottom: 16 }}>{children}</div>
);
const Divider = () => <div style={{ height: 1, background: B, margin: "28px 0" }} />;

/* ── Responsive hook ─────────────────────────────────────────────────────── */
function useIsMobile(breakpoint = 640) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
}

export default function BillingTab({ user, dashState, setToast }) {
  const [loading, setLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [annual, setAnnual] = useState(false);
  const [showRetention, setShowRetention] = useState(false);
  const [switchConfirm, setSwitchConfirm] = useState(null); // { plan, label, losing[] }
  const isMobile = useIsMobile();

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/billing/portal-session`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("nectar_st"),
        },
      });
      const data = await r.json();
      if (r.ok && data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        setToast(data.error || "Could not open billing portal");
        setTimeout(() => setToast(null), 4000);
        setPortalLoading(false);
      }
    } catch {
      setToast("Network error. Please try again.");
      setTimeout(() => setToast(null), 4000);
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async (plan, isAnnual = false) => {
    if (plan === "free") return;
    if (plan === curPlan && !isTrialing) return;
    setLoading(plan);
    try {
      const r = await fetch(`${API_BASE}/app/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("nectar_st"),
        },
        body: JSON.stringify({ plan, annual: isAnnual }),
      });
      const data = await r.json();
      if (r.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setToast(data.error || "Something went wrong. Please try again.");
        setTimeout(() => setToast(null), 4000);
      }
    } catch (e) {
      setToast("Network error. Please try again.");
      setTimeout(() => setToast(null), 4000);
    }
    setLoading(null);
  };

  // Features lost when switching down — keyed by "from → to"
  const featureLoss = {
    "authority→presence": [
      "Your plug post (business-driving CTA)",
    ],
    "pro→authority": [
      "Expert commentary in your field",
      "Jurisdiction-matched professional content",
    ],
    "pro→presence": [
      "Expert commentary in your field",
      "Your plug post",
    ],
    "authority_team→pro": [
      "Team seats and member management",
    ],
    "authority_team→authority": [
      "Team seats and member management",
    ],
    "authority_team→presence": [
      "Team seats and member management",
      "Your plug post",
    ],
    "pro_team→authority_team": [
      "Expert commentary for every member",
    ],
    "pro_team→pro": [
      "Team seats and member management",
    ],
    "pro_team→authority": [
      "Team seats and member management",
      "Expert commentary in your field",
    ],
    "pro_team→presence": [
      "Team seats and member management",
      "Expert commentary in your field",
      "Your plug post",
    ],
  };

  const handleSwitchDown = (targetPlan, targetLabel) => {
    const key = `${curPlan}\u2192${targetPlan}`;
    const losing = featureLoss[key] || [];
    setSwitchConfirm({ plan: targetPlan, label: targetLabel, losing });
  };

  const rawPlan = (user?.plan || "presence").toLowerCase();
  const curPlan = rawPlan;
  const isTrialing = dashState?.is_trialing;
  const trialDays = dashState?.trial_days_remaining || 0;
  const hasActiveSub = user?.account_status === "active" && !isTrialing;
  const planNames = { free: "Free", presence: "Presence", authority: "Authority", pro: "Pro", authority_team: "Authority Team", pro_team: "Authority Pro", agency: "Agency",
    // Legacy slugs for existing users
    authority_pro: "Pro", team: "Authority Team", team_pro: "Authority Pro", team_member: "Authority Pro Member" };
  const planLabel = planNames[rawPlan] || "Presence";

  /* ── Plan definitions ──────────────────────────────────────────────────── */
  // Annual prices: only Presence and Authority have Stripe annual price IDs.
  // annualMonthly = effective monthly rate when billed annually
  // annualTotal   = total annual charge
  // hasAnnualStripe = whether a Stripe annual price ID exists (checkout will use it)
  const individualPlans = [
    { plan: "free", label: "Free", monthly: 0, annualMonthly: 0, annualTotal: 0, hasAnnualStripe: false,
      features: "Regular posts \u00b7 BrandHives content in your voice \u00b7 compliance checker" },
    { plan: "presence", label: "Presence", monthly: 99, annualMonthly: 79.20, annualTotal: 950.40, hasAnnualStripe: true,
      tagline: "Stay consistently visible.",
      features: "Consistent posts from the topics you choose \u00b7 Keeps your presence active and on-brand \u00b7 Everything you need to stay in feed" },
    { plan: "authority", label: "Authority", monthly: 199, annualMonthly: 159.20, annualTotal: 1910.40, hasAnnualStripe: true,
      tagline: "Turn your presence into business.",
      features: "Everything in Presence \u00b7 Plus your plug \u2014 a post that points your audience back to what you offer \u00b7 Stay visible and drive interest in your business" },
    { plan: "pro", label: "Pro", monthly: 299, annualMonthly: 239.20, annualTotal: 2870.40, hasAnnualStripe: true,
      tagline: "Post like the specialist you are.",
      features: "Expert commentary on the developments in your field \u00b7 The rulings, rate moves, and changes your clients care about \u2014 written as your take \u00b7 Positions you as the go-to expert, plus your own topics \u00b7 Everything in Authority" },
  ];

  const teamPlans = [
    { plan: "authority_team", label: "Authority Team", monthly: 149, annualMonthly: 0, annualTotal: 0, hasAnnualStripe: false,
      perSeat: true, minSeats: 2,
      tagline: "Keep your whole team visible.",
      features: "Consistent posts for every member, in their voice \u00b7 Your team stays active and on-brand \u00b7 Plus the plug \u2014 every member drives interest in your business" },
    { plan: "pro_team", label: "Authority Pro", monthly: 199, annualMonthly: 0, annualTotal: 0, hasAnnualStripe: false,
      perSeat: true, minSeats: 2,
      tagline: "Your whole firm, positioned as the experts.",
      features: "Expert commentary in your field, for every member \u00b7 You set your team's field; members can change their own \u00b7 Every member's network sees your firm as the specialists \u00b7 Everything in Authority Team, plus your team's professional content" },
  ];

  const allPlans = [...individualPlans, ...teamPlans];
  const curIdx = allPlans.findIndex(p => p.plan === curPlan);

  /* ── Shared card renderer ──────────────────────────────────────────────── */
  const renderPlanCard = (p) => {
    const planIdx = allPlans.findIndex(ap => ap.plan === p.plan);
    const isCurrent = planIdx === curIdx;
    const isLower = planIdx < curIdx;
    const isHigher = planIdx > curIdx;
    const isFree = p.plan === "free";
    const isLoading = loading === p.plan;

    // Button text + action
    let buttonText = null;
    let buttonAction = null;
    let buttonColor = G;
    let muted = false;

    // When annual is selected but plan has no annual Stripe price, route to monthly
    const effectiveAnnual = annual && p.hasAnnualStripe;

    if (isCurrent && isTrialing) {
      buttonText = "Subscribe Now";
      buttonAction = () => handleUpgrade(p.plan, effectiveAnnual);
    } else if (isCurrent) {
      buttonText = "Current plan";
    } else if (isTrialing && isLower && !isFree) {
      buttonText = "Switch & Subscribe";
      buttonAction = () => handleSwitchDown(p.plan, p.label);
    } else if (!hasActiveSub && isLower) {
      muted = true;
    } else if (!hasActiveSub && isHigher) {
      buttonText = "Upgrade";
      buttonAction = () => handleUpgrade(p.plan, effectiveAnnual);
    } else if (isLower) {
      buttonText = "Downgrade";
      buttonColor = "#999";
      buttonAction = handleManageBilling;
    } else if (isHigher) {
      buttonText = "Upgrade";
      buttonAction = () => handleUpgrade(p.plan, effectiveAnnual);
    }

    const clickable = !!buttonAction && !(isCurrent && !isTrialing) && !muted;
    const showAnnual = annual && p.hasAnnualStripe && p.annualMonthly > 0;
    const displayPrice = showAnnual ? p.annualMonthly : p.monthly;
    const savings = showAnnual ? (p.monthly * 12 - p.annualTotal) : 0;

    const billingLabel = p.perSeat
      ? (showAnnual ? "/seat/mo" : "/seat/mo")
      : (showAnnual ? "/mo" : "/mo");

    return (
      <div
        key={p.plan}
        onClick={() => { if (clickable && !loading) buttonAction(); }}
        style={{
          border: `1px solid ${isCurrent ? G : B}`,
          borderRadius: 12,
          padding: isMobile ? "18px 16px" : "20px 18px",
          background: isCurrent ? "#EBF5FB" : CARD,
          cursor: clickable && !loading ? "pointer" : "default",
          transition: "border-color 0.2s, box-shadow 0.2s",
          opacity: muted ? 0.55 : (loading && !isLoading ? 0.5 : 1),
          display: "flex",
          flexDirection: "column",
          minHeight: isFree ? "auto" : undefined,
        }}
      >
        {/* Plan name */}
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", color: "#1A1A1A" }}>
          {p.label}
          {isCurrent && !isTrialing && (
            <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: G, background: "#EBF5FB", border: `1px solid ${G}`, borderRadius: 4, padding: "2px 7px", verticalAlign: "middle" }}>CURRENT</span>
          )}
        </div>

        {/* Pricing */}
        {isFree ? (
          <div style={{ fontSize: 24, fontWeight: 700, color: G, marginBottom: 10 }}>Free</div>
        ) : (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: G }}>
              ${showAnnual ? (Number.isInteger(displayPrice) ? displayPrice : displayPrice.toFixed(2)) : displayPrice}
              <span style={{ fontSize: 13, color: "#888", fontWeight: 400 }}>{billingLabel}</span>
            </div>
            {/* Annual billing detail */}
            {showAnnual && (
              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                Billed ${p.annualTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{p.perSeat ? " per seat" : ""} annually
                <span style={{ marginLeft: 6, color: GRN, fontWeight: 600 }}>Save ${savings.toFixed(0)}/yr</span>
              </div>
            )}
            {/* Annual not available notice */}
            {annual && !p.hasAnnualStripe && !isFree && (
              <div style={{ fontSize: 11, color: "#999", marginTop: 2, fontStyle: "italic" }}>
                Annual billing coming soon
              </div>
            )}
            {/* Per-seat minimum */}
            {p.perSeat && p.minSeats && (
              <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
                min {p.minSeats} seats · from ${p.monthly * p.minSeats}/mo
              </div>
            )}
            {/* Volume discount for teams */}
            {p.perSeat && (
              <div style={{ fontSize: 11, color: GRN, fontWeight: 600, marginTop: 4 }}>
                20% off with 6+ seats
              </div>
            )}
          </div>
        )}

        {/* Tagline */}
        {p.tagline && (
          <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, fontWeight: 600, color: "#333", marginBottom: 6 }}>
            {p.tagline}
          </div>
        )}

        {/* Features */}
        <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 12, color: "#666", lineHeight: 1.7, flex: 1 }}>
          {p.features}
        </div>

        {/* CTA button */}
        {buttonText && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (clickable && !loading) buttonAction();
            }}
            disabled={!clickable || !!loading}
            aria-label={`${buttonText} — ${p.label}${showAnnual ? " annual" : " monthly"}`}
            style={{
              marginTop: 14,
              padding: "11px 0",
              width: "100%",
              borderRadius: 8,
              border: isCurrent && !isTrialing ? `1px solid ${B}` : "none",
              background: isCurrent && !isTrialing ? "transparent" : buttonColor,
              color: isCurrent && !isTrialing ? "#999" : "#fff",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
              cursor: clickable && !loading ? "pointer" : "default",
              opacity: loading && !isLoading ? 0.5 : 1,
              minHeight: 44,
            }}
          >
            {isLoading ? "Redirecting..." : buttonText}
          </button>
        )}
        {muted && (
          <div style={{ fontSize: 13, color: "#999", fontStyle: "italic", marginTop: 12 }}>
            {isTrialing ? "Not available during trial" : ""}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <SectionLabel>Current plan</SectionLabel>
      <div style={{ background: "#F3F2EF", border: "1px solid #E1E9EE", borderRadius: 12, padding: "20px 22px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 26, marginBottom: 6, fontFamily: "'Playfair Display',Georgia,serif" }}>
              BrandHives <em style={{ color: G }}>{planLabel}</em>
              {isTrialing && <span style={{ marginLeft: 10, background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", borderRadius: 6, fontSize: 11, fontWeight: 700, padding: "3px 10px", verticalAlign: "middle" }}>FREE TRIAL</span>}
            </div>
            <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#666" }}>
              {isTrialing ? `${trialDays} days remaining in your free trial` : `Active plan`}
            </div>
          </div>
        </div>
      </div>

      {user?.comped && !user?.stripe_sub_id ? (
        <div style={{
          background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 12,
          padding: "20px 22px", marginBottom: 24,
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "#166534", marginBottom: 6,
            fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            Complimentary account
          </div>
          <div style={{ fontSize: 13, color: "#15803D",
            fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            Your account has been granted free access. No subscription to manage.
          </div>
        </div>
      ) : (
        <div style={{ background: CARD, border: `1px solid ${B}`, borderRadius: 12, padding: "20px 22px", marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Manage your subscription</div>
          <div style={{ fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 14 }}>
            Update payment method, change plans, view invoices, or cancel — all in one place.
          </div>
          <button
            onClick={handleManageBilling}
            disabled={portalLoading}
            style={{
              background: G,
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
              fontSize: 14,
              fontWeight: 600,
              cursor: portalLoading ? "default" : "pointer",
              opacity: portalLoading ? 0.6 : 1,
              minHeight: 44,
            }}
          >
            {portalLoading ? "Opening..." : "Manage Billing"}
          </button>
        </div>
      )}

      {/* ── Billing cycle toggle ──────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 28, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14, fontWeight: annual ? 400 : 600, color: annual ? "#999" : "#333", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>Monthly</span>
        <button
          onClick={() => setAnnual(!annual)}
          role="switch"
          aria-checked={annual}
          aria-label={annual ? "Switch to monthly billing" : "Switch to annual billing"}
          style={{ position: "relative", width: 52, height: 28, borderRadius: 14, border: "none", background: annual ? G : B, cursor: "pointer", transition: "background 0.2s", padding: 0, flexShrink: 0 }}
        >
          <div style={{ position: "absolute", top: 3, left: annual ? 27 : 3, width: 22, height: 22, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
        </button>
        <span style={{ fontSize: 14, fontWeight: annual ? 600 : 400, color: annual ? "#333" : "#999", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
          Annual
          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#fff", background: GRN, borderRadius: 10, padding: "3px 10px", verticalAlign: "middle" }}>Save 20%</span>
        </span>
      </div>

      {/* ── Plans for individuals ─────────────────────────────────────────── */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            Plans for individuals
          </div>
          <div style={{ fontSize: 13, color: "#777", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginTop: 4, marginBottom: 16 }}>
            Build your personal presence with content written for your goals and expertise.
          </div>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
          gap: 14,
        }}>
          {individualPlans
            .filter(p => p.plan !== "free" || curPlan === "free")
            .map(renderPlanCard)}
        </div>
      </div>

      {/* ── Plans for teams ───────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          borderTop: `2px solid ${B}`,
          paddingTop: 24,
          marginBottom: 6,
        }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#1A1A1A", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}>
            Plans for teams
          </div>
          <div style={{ fontSize: 13, color: "#777", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", marginTop: 4, marginBottom: 16 }}>
            Give every team member a consistent presence. Team pricing is charged per seat, with a two-seat minimum.
          </div>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
          gap: 14,
        }}>
          {teamPlans.map(renderPlanCard)}
        </div>
      </div>

      {/* Cancel / downgrade */}
      {!user?.comped && (
        <>
          <Divider />
          <div style={{ textAlign: "center", padding: "4px 0" }}>
            <button
              onClick={() => setShowRetention(true)}
              style={{ background: "none", border: "none", color: "#999", fontSize: 13, cursor: "pointer", textDecoration: "underline", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" }}
            >
              {hasActiveSub ? "Cancel or downgrade my subscription" : "Cancel my account"}
            </button>
          </div>
        </>
      )}
      {showRetention && (
        <RetentionWaterfall
          user={user}
          API_BASE={API_BASE}
          onClose={() => setShowRetention(false)}
          onCancelled={() => { setShowRetention(false); window.location.reload(); }}
          setToast={setToast}
        />
      )}
      {switchConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setSwitchConfirm(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 14, padding: "28px 26px", maxWidth: 420, width: "90%",
            maxHeight: "90vh", overflowY: "auto",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1A1A1A", marginBottom: 6 }}>
              Switch to {switchConfirm.label}?
            </div>
            <div style={{ fontSize: 14, color: "#555", lineHeight: 1.6, marginBottom: 16 }}>
              You're currently trialing <strong>{planNames[curPlan]}</strong>.
              Switching to <strong>{switchConfirm.label}</strong> means you'll lose:
            </div>
            {switchConfirm.losing.length > 0 && (
              <ul style={{ margin: "0 0 18px 0", padding: "0 0 0 20px" }}>
                {switchConfirm.losing.map((f, i) => (
                  <li key={i} style={{ fontSize: 13, color: "#D85A30", lineHeight: 1.8 }}>{f}</li>
                ))}
              </ul>
            )}
            <div style={{ fontSize: 13, color: "#666", marginBottom: 20, lineHeight: 1.5 }}>
              If you just want to be present without the authority features, {switchConfirm.label} is a great fit.
              You can always upgrade later.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setSwitchConfirm(null)} style={{
                flex: 1, padding: "12px 0", borderRadius: 8, border: `1px solid ${B}`,
                background: "#fff", color: "#333", fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}>
                Keep {planNames[curPlan]}
              </button>
              <button onClick={() => { setSwitchConfirm(null); handleUpgrade(switchConfirm.plan, annual); }} style={{
                flex: 1, padding: "12px 0", borderRadius: 8, border: "none",
                background: G, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
              }}>
                Switch to {switchConfirm.label}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
